import React, { useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { APPT_TYPES, saveAppointment, apptLabels, logActivity, optedOutTexts, phoneE164 } from '../../services/crmService';
import { NOTIFICATIONS_ENABLED } from '../../config/notifications';
import { applyMerge } from './merge';
import { ui, DANGER } from './crmUi';

// Opened after an "Appointment set" call result. Step 1: details. Step 2: confirmations.
const pad = (n) => String(n).padStart(2, '0');
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const DEFAULT_EMAIL_SUBJECT = 'Confirmed: our {{appt_type}} appointment on {{appt_date}}';
const DEFAULT_EMAIL = 'Hi {{first_name|there}},\n\nThis confirms our {{appt_type}} appointment on {{appt_date}} at {{appt_time}}.{{appt_extra}}\n\nLooking forward to it!\n{{agent_name}}\nRESIDENCE | eXp Realty';
const DEFAULT_TEXT = 'Hi {{first_name|there}}, this is {{agent_name}} confirming our {{appt_type}} appointment {{appt_date}} at {{appt_time}}.{{appt_extra}}';
const REMINDER_SUBJECT = 'Reminder: our appointment today at {{appt_time}}';
const REMINDER_EMAIL = 'Hi {{first_name|there}}, a quick reminder about our {{appt_type}} appointment today at {{appt_time}}.{{appt_extra}}\n\n{{agent_name}}';
const REMINDER_TEXT = 'Reminder: our {{appt_type}} appointment is today at {{appt_time}}.{{appt_extra}} - {{agent_name}}';
// {{appt_extra}} expands to the location or Zoom link sentence.
const fill = (tpl, contact, appt, agent) => {
  const L = apptLabels(appt);
  const extra = appt.type === 'In person' && appt.location ? ` Location: ${appt.location}.` : appt.type === 'Zoom' && appt.zoomLink ? ` Zoom link: ${appt.zoomLink}` : '';
  return applyMerge(tpl.replace(/\{\{appt_extra\}\}/g, extra), { ...contact, _appt: L }, agent);
};

export default function AppointmentModal({ contact, uid, isAdmin, onDone }) {
  const [agent, setAgent] = useState({});
  const [form, setForm] = useState({ date: tomorrow(), time: '10:00', type: 'Phone', location: '', zoomLink: '', notes: '' });
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [textBody, setTextBody] = useState('');
  useEffect(() => {
    if (!uid) return;
    get(ref(database, `users/${uid}`)).then((s) => {
      const u = s.val() || {}; const p = u.profile || {};
      setAgent({ ...p, displayName: u.displayName });
      setForm((f) => ({ ...f, zoomLink: f.zoomLink || p.zoomLink || '' }));
    }).catch(() => {});
  }, [uid]);
  if (!contact) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const startAt = new Date(`${form.date}T${form.time || '00:00'}`);
  const apptPreview = { ...form, startAt: startAt.toISOString() };

  const save = async () => {
    if (!form.date || !form.time || isNaN(startAt)) { setMsg('Pick a date and time.'); return; }
    if (form.type === 'Zoom' && !form.zoomLink.trim()) { setMsg('Add a Zoom link (save yours in My Profile to fill it in automatically).'); return; }
    setBusy(true); setMsg('');
    try {
      const reminders = { subject: fill(REMINDER_SUBJECT, contact, apptPreview, agent), email: fill(REMINDER_EMAIL, contact, apptPreview, agent), text: fill(REMINDER_TEXT, contact, apptPreview, agent) };
      const a = await saveAppointment(contact, apptPreview, uid, isAdmin, reminders);
      setSaved(a);
      setEmailSubject(fill(DEFAULT_EMAIL_SUBJECT, contact, a, agent));
      setEmailBody(fill(DEFAULT_EMAIL, contact, a, agent));
      setTextBody(fill(DEFAULT_TEXT, contact, a, agent));
    } catch (e) { setMsg(e.message || 'Could not save.'); }
    setBusy(false);
  };
  const canEmail = contact.email && !contact.email_opt_out;
  const canText = phoneE164(contact.phone) && !optedOutTexts(contact);
  const sendConfirm = async (channel) => {
    setBusy(true);
    try {
      const text = channel === 'email' ? `Confirmation email${NOTIFICATIONS_ENABLED ? '' : ' (not sent: email sending is off)'}: ${emailSubject}` : `Confirmation text${NOTIFICATIONS_ENABLED ? '' : ' (not sent: texting is off)'}: ${textBody}`;
      await logActivity(contact.id, { type: channel, appointmentId: saved.id, subject: channel === 'email' ? emailSubject : null, notes: channel === 'email' ? emailBody : textBody, sent: NOTIFICATIONS_ENABLED, text }, uid);
      setMsg(`${channel === 'email' ? 'Email' : 'Text'} logged.`);
    } catch (e) { setMsg(e.message || 'Could not log it.'); }
    setBusy(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1100 }}>
      <div role="dialog" aria-modal="true" style={{ background: '#FFFFFF', borderRadius: 12, padding: 24, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', boxSizing: 'border-box' }}>
        <h3 style={ui.sectionTitle}>Appointment with {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name}</h3>
        {!saved ? (<>
          <div style={ui.grid(150)}>
            <div><label style={ui.label}>Date</label><input type="date" style={ui.input} value={form.date} onChange={set('date')} /></div>
            <div><label style={ui.label}>Time</label><input type="time" style={ui.input} value={form.time} onChange={set('time')} /></div>
          </div>
          <label style={ui.label}>Type</label>
          <div style={ui.row}>{APPT_TYPES.map((t) => (
            <label key={t} style={{ display: 'flex', gap: 6, alignItems: 'center', minHeight: 32 }}><input type="radio" name="appt-type" checked={form.type === t} onChange={() => setForm({ ...form, type: t })} /> {t}</label>
          ))}</div>
          {form.type === 'In person' && (<><label style={ui.label}>Location</label><input style={ui.input} placeholder="Address or meeting spot" value={form.location} onChange={set('location')} /></>)}
          {form.type === 'Zoom' && (<><label style={ui.label}>Zoom link</label><input style={ui.input} placeholder="Save yours in My Profile" value={form.zoomLink} onChange={set('zoomLink')} />
            {!agent.zoomLink && <div style={{ fontSize: 12, color: '#666' }}>Tip: add your Zoom meeting link in My Profile and it fills in here.</div>}</>)}
          <label style={ui.label}>Notes (optional)</label>
          <textarea style={{ ...ui.input, minHeight: 60 }} value={form.notes} onChange={set('notes')} />
          <p style={{ fontSize: 12, color: '#666' }}>Saving adds a task for that day, and queues a reminder email and text 2 hours before (skipping anything they opted out of).</p>
          <div style={ui.row}>
            <button style={ui.btn} disabled={busy} onClick={save}>Save appointment</button>
            <button style={ui.ghost} disabled={busy} onClick={onDone}>Skip</button>
          </div>
        </>) : (<>
          <p style={ui.sub}>Saved: {apptLabels(saved).date} at {apptLabels(saved).time} ({saved.type}). Task added{saved.reminders?.length ? '' : ''}.</p>
          {!NOTIFICATIONS_ENABLED && <div style={ui.notice}>Sending isn't turned on yet. These log on the contact's timeline; nothing goes out. Email reminders are queued and will send once email sending and the scheduler are set up.</div>}
          <label style={ui.label}>Confirmation email</label>
          {canEmail ? (<>
            <input style={ui.input} value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
            <textarea style={{ ...ui.input, minHeight: 120 }} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
            <button style={{ ...ui.btn, marginTop: 6 }} disabled={busy} onClick={() => sendConfirm('email')}>Send confirmation email</button>
          </>) : <div style={{ color: DANGER, fontSize: 13 }}>{contact.email ? 'Opted out of email.' : 'No email address.'}</div>}
          <label style={ui.label}>Confirmation text</label>
          {canText ? (<>
            <textarea style={{ ...ui.input, minHeight: 80 }} value={textBody} onChange={(e) => setTextBody(e.target.value)} />
            <button style={{ ...ui.btn, marginTop: 6 }} disabled={busy} onClick={() => sendConfirm('text')}>Send confirmation text</button>
          </>) : <div style={{ color: DANGER, fontSize: 13 }}>{contact.phone ? 'Opted out of texts.' : 'No phone number.'}</div>}
          {msg && <div style={{ fontSize: 13, marginTop: 8 }}>{msg}</div>}
          <button style={{ ...ui.ghost, marginTop: 12 }} onClick={onDone}>Done</button>
        </>)}
        {!saved && msg && <div style={{ color: DANGER, fontSize: 13, marginTop: 8 }}>{msg}</div>}
      </div>
    </div>
  );
}
